import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import { fetchSiteConfig, updateSiteConfig, sendGlobalNotification } from "../services/SiteConfigService";

const ManageSiteConfigPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState<any>(null);
  const [notificationSending, setNotificationSending] = useState(false);
  const [notificationData, setNotificationData] = useState({ title: '', body: '' });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await fetchSiteConfig();
      setConfig(data);
    } catch (err) {
      setError("Failed to load site configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfig({
      ...config,
      homePageMode: e.target.value
    });
  };

  const handleBasicConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setConfig({
      ...config,
      basicHomeConfig: {
        ...config.basicHomeConfig,
        [name]: type === "checkbox" ? checked : value
      }
    });
  };

  const handleTheaterConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked } = e.target;
    setConfig({
      ...config,
      basicTheaterConfig: {
        ...config.basicTheaterConfig,
        [name]: type === "checkbox" ? checked : e.target.value
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await updateSiteConfig(config);
      setSuccess("Site configuration updated successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError("Failed to update site configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNotificationData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notificationData.title.trim() || !notificationData.body.trim()) {
      setError("Please enter both title and message for the notification");
      return;
    }

    try {
      setNotificationSending(true);
      setError(null);
      const result = await sendGlobalNotification(notificationData);
      setSuccess(`Global notification sent to ${result.result.successCount} users successfully`);
      setNotificationData({ title: '', body: '' });
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError("Failed to send global notification");
    } finally {
      setNotificationSending(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (!config) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          Failed to load site configuration
        </Alert>
      </Container>
    );
  }

  return (
    <PermissionWrapper pageName="ManageSiteConfigPage">
      <Container>
        <h2 className="mb-4">
          <i className="bi bi-gear-fill me-2"></i>
          Site Configuration
        </h2>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Homepage Mode</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group>
                <div className="mb-3">
                  <Form.Check
                    type="radio"
                    id="basic-mode"
                    name="homePageMode"
                    label="Basic Mode (Simple content-focused layout)"
                    value="basic"
                    checked={config.homePageMode === "basic"}
                    onChange={handleModeChange}
                  />
                  <Form.Text className="text-muted">
                    Shows a simplified homepage with content sections and YouTube videos
                  </Form.Text>
                </div>
                <div>
                  <Form.Check
                    type="radio"
                    id="advanced-mode"
                    name="homePageMode"
                    label="Advanced Mode (Full-featured interactive layout)"
                    value="advanced"
                    checked={config.homePageMode === "advanced"}
                    onChange={handleModeChange}
                  />
                  <Form.Text className="text-muted">
                    Shows the standard homepage with all interactive components
                  </Form.Text>
                </div>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Basic Homepage Configuration</h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Hero Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="heroTitle"
                      value={config.basicHomeConfig.heroTitle}
                      onChange={handleBasicConfigChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Hero Subtitle</Form.Label>
                    <Form.Control
                      type="text"
                      name="heroSubtitle"
                      value={config.basicHomeConfig.heroSubtitle}
                      onChange={handleBasicConfigChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <h6 className="mb-3">Sections to Display</h6>
              <Row>
                <Col md={4}>
                  <Form.Check
                    type="checkbox"
                    id="show-youtube"
                    name="showYoutubeSection"
                    label="YouTube Videos Section"
                    checked={config.basicHomeConfig.showYoutubeSection}
                    onChange={handleBasicConfigChange}
                    className="mb-2"
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="checkbox"
                    id="show-news"
                    name="showNewsSection"
                    label="News Section"
                    checked={config.basicHomeConfig.showNewsSection}
                    onChange={handleBasicConfigChange}
                    className="mb-2"
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="checkbox"
                    id="show-articles"
                    name="showArticlesSection"
                    label="Articles Section"
                    checked={config.basicHomeConfig.showArticlesSection}
                    onChange={handleBasicConfigChange}
                    className="mb-2"
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="checkbox"
                    id="show-reviews"
                    name="showReviewsSection"
                    label="Reviews Section"
                    checked={config.basicHomeConfig.showReviewsSection}
                    onChange={handleBasicConfigChange}
                    className="mb-2"
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="checkbox"
                    id="show-tutorials"
                    name="showTutorialsSection"
                    label="Tutorials Section"
                    checked={config.basicHomeConfig.showTutorialsSection}
                    onChange={handleBasicConfigChange}
                    className="mb-2"
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Statistics Configuration</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-3">Configure which statistics to display in the hero section</p>
              <Row>
                <Col md={3}>
                  <Form.Check
                    type="checkbox"
                    id="show-states-count"
                    name="showStatesCount"
                    label="Active States Count"
                    checked={config.basicHomeConfig?.showStatesCount !== false}
                    onChange={handleBasicConfigChange}
                    className="mb-2"
                  />
                </Col>
                <Col md={3}>
                  <Form.Check
                    type="checkbox"
                    id="show-groups-count"
                    name="showGroupsCount"
                    label="Theater Groups Count"
                    checked={config.basicHomeConfig?.showGroupsCount !== false}
                    onChange={handleBasicConfigChange}
                    className="mb-2"
                  />
                </Col>
                <Col md={3}>
                  <Form.Check
                    type="checkbox"
                    id="show-programs-count"
                    name="showProgramsCount"
                    label="Plays Count"
                    checked={config.basicTheaterConfig?.showProgramsCount !== false}
                    onChange={handleTheaterConfigChange}
                    className="mb-2"
                  />
                </Col>
                <Col md={3}>
                  <Form.Check
                    type="checkbox"
                    id="show-schedules-count"
                    name="showSchedulesCount"
                    label="Schedules Count"
                    checked={config.basicHomeConfig?.showSchedulesCount !== false}
                    onChange={handleBasicConfigChange}
                    className="mb-2"
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-end">
            <Button 
              type="submit" 
              variant="primary" 
              disabled={saving}
              className="px-4"
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>Save Configuration</>
              )}
            </Button>
          </div>
        </Form>

        <Card className="mt-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-bell-fill me-2"></i>
              Global Push Notification
            </h5>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3">
              Send an instant push notification to all users who have the site open in their browser.
            </p>
            <Form onSubmit={handleSendNotification}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Notification Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={notificationData.title}
                      onChange={handleNotificationChange}
                      placeholder="Enter notification title"
                      maxLength={50}
                    />
                    <Form.Text className="text-muted">
                      {notificationData.title.length}/50 characters
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Notification Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="body"
                      value={notificationData.body}
                      onChange={handleNotificationChange}
                      placeholder="Enter notification message"
                      maxLength={200}
                    />
                    <Form.Text className="text-muted">
                      {notificationData.body.length}/200 characters
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex justify-content-end">
                <Button 
                  type="submit" 
                  variant="warning" 
                  disabled={notificationSending || !notificationData.title.trim() || !notificationData.body.trim()}
                  className="px-4"
                >
                  {notificationSending ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send-fill me-2"></i>
                      Send Global Notification
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </PermissionWrapper>
  );
};

export default ManageSiteConfigPage;